import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { toast } from "sonner";
import { Plus, Trash2, Clock } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function AddForm({ reps, regions, onAdd }) {
  const [rep, setRep] = useState("");
  const [region, setRegion] = useState("");
  const [days, setDays] = useState([]);
  const [start, setStart] = useState(9);
  const [end, setEnd] = useState(18);
  const toggle = (d) => setDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]));
  const submit = () => {
    if (!rep || !region || days.length === 0) { toast.error("Rep, region and at least one day required"); return; }
    onAdd({ rep_id: rep, region, days_of_week: days, start_hour: +start, end_hour: +end });
    setDays([]);
  };
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="coverage-add-form">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">Add Coverage Entry</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Rep</label>
          <select value={rep} onChange={(e) => setRep(e.target.value)} data-testid="cov-rep"
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
            <option value="">Select rep…</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Region</label>
          <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="cov-region"
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
            <option value="">Select region…</option>
            {regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Start Hour</label>
          <input type="number" min="0" max="23" value={start} onChange={(e) => setStart(e.target.value)}
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530]" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">End Hour</label>
          <input type="number" min="0" max="23" value={end} onChange={(e) => setEnd(e.target.value)}
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530]" />
        </div>
      </div>
      <div className="flex gap-1 mb-3">
        {DAYS.map((d) => (
          <button key={d} onClick={() => toggle(d)}
            className={`w-11 py-1.5 font-mono text-[10px] uppercase border transition-colors ${days.includes(d) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>
            {d}
          </button>
        ))}
      </div>
      <button onClick={submit} data-testid="cov-add-btn"
        className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]">
        <Plus className="w-3.5 h-3.5" /> Add Entry
      </button>
    </div>
  );
}

export default function Coverage() {
  const [entries, setEntries] = useState(null);
  const [reps, setReps] = useState([]);
  const [regions, setRegions] = useState([]);

  const load = () => api.get("/coverage").then((r) => setEntries(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "rep")));
    api.get("/regions").then((r) => setRegions(r.data));
  }, []);

  const add = async (body) => {
    try { await api.post("/coverage", body); toast.success("Coverage added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/coverage/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const byRep = {};
  (entries || []).forEach((e) => { (byRep[e.rep_name] = byRep[e.rep_name] || []).push(e); });

  return (
    <>
      <PageHeader eyebrow="ALLOCATION + BOOKING GATE · MULTI-REGION · OVERLAPPING" title="Rep Coverage Schedule" hint="When each rep is on-shift for which regions. Allocation suggestions and meeting booking only offer reps who are covered at that moment." />
      <div className="p-8 space-y-6">
        <div className="font-mono text-[11px] text-[#1C2530]/50 leading-relaxed max-w-3xl">
          A rep can hold multiple simultaneous coverage entries across regions (e.g. India Mon–Fri 9–18 AND GCC Sun–Thu 9–18).
          Allocation and meeting booking only allow a rep for a lead whose region has an <span className="text-[#1C2530]">active</span> entry at that day/time (evaluated in the region's own timezone).
        </div>
        <AddForm reps={reps} regions={regions} onAdd={add} />
        {!entries ? <Loading /> : Object.keys(byRep).length === 0 ? <Empty label="NO_COVERAGE_ENTRIES" /> : (
          <div className="space-y-4" data-testid="coverage-list">
            {Object.entries(byRep).map(([name, list]) => (
              <div key={name} className="border border-[#DFDAD0] bg-white p-5">
                <div className="text-sm font-medium text-[#1C2530] mb-3">{name}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {list.map((e) => (
                    <div key={e.id} className="flex items-center justify-between border border-[#DFDAD0] px-3 py-2" data-testid={`cov-entry-${e.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-semibold uppercase text-[#1C2530]">{e.region}</span>
                          <span className="flex items-center gap-1 font-mono text-[10px] text-[#1C2530]/60"><Clock className="w-3 h-3" />{e.start_hour}:00–{e.end_hour}:00</span>
                        </div>
                        <div className="flex gap-1 mt-1">
                          {DAYS.map((d) => (
                            <span key={d} className={`font-mono text-[9px] px-1 ${e.days_of_week.includes(d) ? "bg-[#3E8F99] text-white" : "text-[#1C2530]/25"}`}>{d}</span>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => del(e.id)} data-testid={`cov-del-${e.id}`} className="text-[#B23A2E] hover:bg-[#B23A2E]/10 p-1.5"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
